<script setup lang="ts">
  // Components
  import { Checkbox, IconImage } from '@/shared/ui';
  import DocumentListActions from './DocumentListActions.vue';
  import DocumentModulesCell from './DocumentModulesCell.vue';
  import PhysicalLocationOverflowCell from './PhysicalLocationOverflowCell.vue';
  // Composables
  import {
    DocumentFormat,
    getDocumentRevisionStatusLabel,
    isExternalDocument,
    type DocumentRevision,
  } from '@/entities/Document';
  import { formatCivilDateToPtBR } from '@/shared/lib';
  import { getDocumentRevisionStatusClasses } from '@/shared/lib/utils';
  import SmallChevronUpIcon from '@/shared/assets/icons/SmallChevronUp.svg';
  import { buildPhysicalLocationOverflowDisplay } from '../model/physical-location-overflow';

  withDefaults(
    defineProps<{
      revision: DocumentRevision;
      isSelected: boolean;
      hasOlderRevisions?: boolean;
      isExpanded?: boolean;
      isChild?: boolean;
    }>(),
    {
      hasOlderRevisions: false,
      isExpanded: false,
      isChild: false,
    }
  );

  const emit = defineEmits<{
    'toggle-selection': [id: number];
    'toggle-expand': [];
    delete: [id: number];
    replace: [revision: DocumentRevision];
  }>();

  const toggleDocument = (id: number) => {
    emit('toggle-selection', id);
  };

  const getLocalHtmDisplay = (rev: DocumentRevision) => {
    if (rev.config?.format === DocumentFormat.EL) {
      return buildPhysicalLocationOverflowDisplay([]);
    }

    return buildPhysicalLocationOverflowDisplay(rev.physicalLocations);
  };
</script>

<template>
  <tr
    class="table-row-hover transition-colors duration-300 ease-out"
    :class="{ 'bg-[rgba(227,227,227,0.30)]': isChild }"
    :data-testid="
      isChild ? `document-revision-child-row-${revision.id}` : `document-row-${revision.id}`
    "
  >
    <td class="w-20 align-middle">
      <div class="flex justify-center items-center">
        <Checkbox
          :id="revision.id"
          :checked="isSelected"
          @toggle="toggleDocument(revision.id)"
        />
      </div>
    </td>
    <!-- Nome -->
    <td class="pl-6 py-4.5 pr-0">
      <div class="flex items-center gap-2">
        <div class="text-sm text-gray-07">
          {{
            revision.document.name && revision.document.prefix
              ? revision.document.prefix + ' - ' + revision.document.name
              : revision.document.name || revision.document.prefix
          }}
        </div>
      </div>
    </td>
    <!-- Revisão -->
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left">
      {{ isExternalDocument(revision) ? '-' : revision.revision }}
    </td>
    <!-- Status -->
    <td class="pl-6 py-4.5">
      <div class="flex items-center">
        <span
          v-if="revision.status"
          :class="getDocumentRevisionStatusClasses(revision.status)"
          class="text-center"
        >
          {{ getDocumentRevisionStatusLabel(revision) }}
        </span>
        <span
          v-else
          class="px-3 py-1 rounded-full text-xs/3.5 font-medium inline-block w-full bg-gray-100 text-gray-600"
        >
          N/A
        </span>
      </div>
    </td>
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left">
      {{ formatCivilDateToPtBR(revision.effectiveDate) }}
    </td>
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left">
      {{ formatCivilDateToPtBR(revision.validityDate) }}
    </td>
    <!-- Formato: TODO: Buscar o formato correto -->
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left">
      {{ revision?.config?.format ? revision.config.format.replace('_', '/') : '-' }}
    </td>
    <!-- Local HTM -->
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left">
      <PhysicalLocationOverflowCell :display="getLocalHtmDisplay(revision)" />
    </td>
    <!-- Módulo -->
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left">
      <DocumentModulesCell :modules="revision.directoryModules" />
    </td>

    <!-- Ações -->
    <DocumentListActions
      :revision="revision"
      class="relative"
      @delete="emit('delete', revision.id)"
      @replace="emit('replace', $event)"
    />
    <td class="pr-5 py-4.5 text-right align-middle w-16">
      <button
        v-if="hasOlderRevisions"
        type="button"
        class="inline-flex size-8 items-center justify-center rounded-lg border border-gray-02 bg-white-01 shadow-sm"
        :aria-expanded="isExpanded"
        :aria-label="isExpanded ? 'Recolher revisões' : 'Expandir revisões'"
        :data-testid="`document-revisions-expand-${revision.documentId}`"
        @click="emit('toggle-expand')"
      >
        <IconImage
          :src="SmallChevronUpIcon"
          alt=""
          class="transition-transform duration-200"
          :class="{ 'rotate-180': !isExpanded }"
        />
      </button>
    </td>
  </tr>
</template>
